import type { InputFrame, RecordedRun, RunDescriptor, SimulationState } from '../contracts/game'
import type { GameplayTuning } from './GameplayTuning'
import { Simulation } from './Simulation'
import type { RunFactory } from '../game/RunFactory'

export class ReplayRecorder {
  private readonly descriptor: RunDescriptor
  private readonly inputs: InputFrame[] = []
  private previousSteeringQ: number | null = null

  public constructor(descriptor: RunDescriptor) {
    this.descriptor = { ...descriptor }
  }

  public record(input: InputFrame): void {
    if (input.steeringQ === this.previousSteeringQ) return
    this.inputs.push({ ...input })
    this.previousSteeringQ = input.steeringQ
  }

  public finish(state: Readonly<SimulationState>, stateHash = ''): RecordedRun {
    return {
      descriptor: { ...this.descriptor },
      inputs: this.inputs.map((input) => ({ ...input })),
      tickCount: state.tick,
      distance: state.player.distance,
      deathCause: state.player.deathCause,
      stateHash,
    }
  }
}

export function replayRun(recorded: RecordedRun, tuning: GameplayTuning): Simulation {
  const simulation = new Simulation(tuning)
  simulation.start(recorded.descriptor)
  let inputIndex = 0
  let steeringQ = 0

  for (let tick = 0; tick < recorded.tickCount; tick += 1) {
    const nextInput = recorded.inputs[inputIndex]
    if (nextInput?.tick === tick) {
      steeringQ = nextInput.steeringQ
      inputIndex += 1
    }
    simulation.step({ tick, steeringQ })
  }
  return simulation
}

export function replayRunWithFactory(recorded: RecordedRun, factory: RunFactory): Simulation {
  const simulation = factory.create(recorded.descriptor).simulation
  let inputIndex = 0
  let steeringQ = 0
  for (let tick = 0; tick < recorded.tickCount; tick += 1) {
    const nextInput = recorded.inputs[inputIndex]
    if (nextInput?.tick === tick) {
      steeringQ = nextInput.steeringQ
      inputIndex += 1
    }
    simulation.step({ tick, steeringQ })
  }
  return simulation
}
